// Mensagens entre o aparelho e o servidor da mesa: JSON, uma por quadro do WebSocket.
// O aparelho só pede; quem decide é o servidor, sempre pelo motor de @truco6/regras.
import { ofensivo } from '@truco6/moderacao';
import { GESTURES } from '@truco6/regras';
import type { GanhosDaPartida } from '@truco6/gamificacao';
import type { Plano } from '@truco6/planos';
import type {
  Card, ConfirmOption, Gesture, HandResult, HandValue, HouseRules, Pending, Phase, PlayerView, Rank, RoundResult, Seat, SignalVisibility, Special, Team, VisibleEvent, VoteAct,
  VoteKind, VoteOption,
} from '@truco6/regras';

export const VERSAO_PROTOCOLO = 1;
export const TAMANHO_MAXIMO = 4096; // bytes por mensagem do aparelho
// Quanto a mesa espera pela animação dos aparelhos: as cartas sendo dadas e o LEVOU no fim da rodada.
export const ANIMACAO = { distribuicao: 3_400, pausaRodada: 1_800 } as const;
export const REACOES = ['😂', '🔥', '👏', '😱', '🤡', '😎', '🙏', '💪'] as const;
export const OPINIOES = ['👍', '👎', '🔥'] as const;
// O voto do bot num trio com pessoa vira opinião: aceitar 👍, correr 👎, pedir o próximo valor 🔥.
export const OPINIAO_DO_VOTO: Readonly<Record<VoteOption, (typeof OPINIOES)[number]>> = { accept: '👍', run: '👎', raise: '🔥' };
// Frases prontas da aba Reações, em duas abas: Frases e Zoeira (para zoar o trio adversário). Sem chat livre e sem
// frase própria; 5 s entre frases do mesmo jogador, somando as duas abas. Mudar a lista é mudar a constante e o teste.
export const FRASES = ['Pé deu, pé se vira', 'Deixa aqui', 'Vou fechar ele', 'Faz aí'] as const;
export const ZOEIRA = ['CHUPAAA', 'Vocês são marrecos', 'Tá roubando', 'Inocente', 'Freguês!', 'Pode chorar'] as const;
export const INTERVALO_FRASE_MS = 5_000;
export const ehZoeira = (frase: string): boolean => (ZOEIRA as readonly string[]).includes(frase);
// Plateia: até 20 pessoas por sala, cada uma com uma reação a cada 5 s. O campeonato pode ter 60 s de atraso.
export const PLATEIA_MAXIMA = 20;
export const INTERVALO_REACAO_PLATEIA_MS = 5_000;
export const ATRASO_DA_PLATEIA_MS = 60_000;

// Limite do Grátis na mesa, nas mensagens de erro: sem partidas hoje (sem_partidas) e banco sem resposta depois das
// tentativas (limite_indisponivel, que o jogo mostra num aviso, sem abrir a página do limite).
export const MENSAGEM_SEM_PARTIDAS = 'Suas partidas grátis de hoje acabaram.';
export const MENSAGEM_LIMITE_INDISPONIVEL = 'Não deu para conferir suas partidas agora. Tente de novo em instantes.';

// Gritos que o jogador pode gravar com a própria voz (planos Prata e Ouro).
export const GRITOS = ['truco', 'seis', 'nove', 'doze'] as const;
export type Grito = (typeof GRITOS)[number];

export interface Perfil {
  apelido: string;
  avatar: string | null;
  gritos?: Partial<Record<Grito, string>>; // versão de cada grito gravado; o arquivo fica no Supabase
}

// Por onde a pessoa chegou à vaga aberta (evento vaga_ocupada): a lista de salas, o link ou código, o status ou o
// chamado de um amigo, a busca da liga ou o botão Sentar na vaga de quem estava assistindo.
export const ORIGENS_DA_VAGA = ['lista', 'link', 'amigo', 'busca', 'espectador'] as const;
export type OrigemDaVaga = (typeof ORIGENS_DA_VAGA)[number];

// ---- aparelho → servidor ----
export type MensagemCliente =
  | { t: 'ola'; token: string }
  | { t: 'criar'; regras?: Partial<HouseRules>; nome?: string; publica?: boolean; ingresso?: number | null; plateia?: boolean; comunidade?: string } // plateia: sala privada com plateia (padrão ligada); comunidade: mesa só para os membros
  | { t: 'entrar'; codigo: string; perfil: Perfil; origem?: OrigemDaVaga }
  | { t: 'sentar'; lugar: Seat; origem?: OrigemDaVaga }
  | { t: 'levantar' }
  | { t: 'iniciar' }
  | { t: 'sair' }
  | { t: 'jogar'; carta: Card; coberta?: boolean }
  | { t: 'truco' }
  | { t: 'votar'; opcao: VoteOption }
  | { t: 'confirmar'; opcao: ConfirmOption }
  | { t: 'sinal'; gesto: Gesture } // sempre para os dois parceiros
  | { t: 'reacao'; emoji: string }
  | { t: 'opiniao'; emoji: string }
  | { t: 'frase'; frase: string }
  | { t: 'reservar' } // sala valendo Tentos: reserva o ingresso para sentar (quem está em pé, ou depois de um débito desfeito)
  | { t: 'revanche'; sim: boolean } // fim da partida: Sim fica para a próxima (valendo Tentos, reserva o ingresso); Não levanta
  | { t: 'bots'; sim: boolean } // revanche incompleta: o dono responde "Deseja iniciar com bots?"
  | { t: 'buscar'; perfil: Perfil; trio?: string } // busca de mesa da Liga da Semana, sozinho ou com um trio fixo
  | { t: 'campeonato'; jogo: string } // jogo de campeonato: abre (ou acha) a sala do confronto; a resposta é 'criada'
  | { t: 'cancelar_busca' }
  | { t: 'sincronizar' }
  | { t: 'ping' }
  // Plateia: assistir abre um canal separado, que nunca leva cartas da mão, sinais nem votos. O dono da sala privada
  // tira alguém da plateia, e a pessoa não volta para esta sala.
  // Pelo código (lista de salas, link) ou pelo amigo que está jogando (aba Amigos): a sala privada nunca manda o
  // código para a aba Amigos, e a mesa acha a sala pelo amigo.
  | { t: 'assistir'; codigo?: string; amigo?: string; perfil: Perfil }
  | { t: 'reacao_plateia'; emoji: string }
  | { t: 'tirar_da_plateia'; usuario: string }
  | { t: 'recebi'; seq: number }; // o aparelho confirma a atualização: mede a sincronização para o painel

// ---- servidor → aparelho ----
export type FaseSala = 'espera' | 'jogo' | 'fim' | 'encerrada'; // encerrada: a partida acabou sem o dono na mesa
// distribuicao e rodada: a mesa espera a animação das cartas dadas e do LEVOU.
// entrada: liga com patrocinador, a tela de entrada antes das cartas da primeira mão.
// contagem: o 3, 2, 1 da revanche, antes de a partida começar.
export type TipoPrazo = 'vez' | 'voto' | 'confirmacao' | 'inicio' | 'reconexao' | 'distribuicao' | 'rodada' | 'revanche' | 'entrada' | 'contagem';

export interface Prazo {
  tipo: TipoPrazo;
  ate: number; // horário do servidor, em ms
  lugar?: Seat;
  trio?: Team;
}

// Lugar de quem cai ou sai: nos 45 s da queda, o lugar fica guardado (conectado false, sem bot, com o prazo de
// reconexão). Depois, ou na hora da saída, o bot joga e o lugar vira vaga aberta. Quem senta na vaga entra na
// próxima mão (entraNaMao); até lá, o bot termina a mão em andamento. Sala valendo Tentos não abre vaga.
export type LugarPublico =
  | {
    tipo: 'pessoa'; id: string; perfil: Perfil; conectado: boolean; bot: boolean; botApelido?: string; nivel?: number; moldura?: string; // com bot no lugar: Bot1, Bot2... nivel: Rodagem; moldura: o cosmético do rosto
    coroa?: true; // fechou a semana passada em 1º no Rei do Pote
    vagaAberta?: true; entraNaMao?: number;
  }
  | { tipo: 'bot'; perfil: Perfil; vagaAberta?: true }; // vagaAberta: com a partida andando, a vaga com bot aceita gente

export interface SalaPublica {
  codigo: string;
  nome: string;
  publica: boolean; // pública aparece na lista de salas; privada entra só por link ou código
  dono: string;
  fase: FaseSala;
  partida: number;
  regras: HouseRules;
  lugares: (LugarPublico | null)[];
  esperando: { id: string; perfil: Perfil }[];
  tentos: TentosDaSala | null; // null: sala grátis
  removida?: true; // removida na revisão do nome: encerra no fim da partida
  revanche?: { ate: number; sim: string[] } | null; // fim da partida: até quando confirmar e quem já disse Sim
  confirmarBots?: true; // revanche incompleta: o dono vê "Deseja iniciar com bots?" (os outros, que ele está decidindo)
  liga?: LigaDaSala | null; // mesa da Liga da Semana: regras fixas, sem dono e sem Tentos
  visual?: { mesa: string; verso: string } | null; // sala privada: a mesa e o verso do dono; null: os da casa (ou os da liga)
  placar?: [number, number]; // partida em andamento: o placar de cada trio, para quem assiste e vê a vaga aberta
  plateia?: PessoaNaPlateia[]; // quem está assistindo, para o "👀 3" dos jogadores (bot nunca assiste)
  plateiaAberta?: boolean; // sala privada: o dono escolheu ter plateia (pública e valendo Tentos pública: sempre)
  campeonato?: CampeonatoDaSala | null; // sala de campeonato: o confronto, sem dono e sem vaga
}

// O confronto de campeonato na sala: os dois trios (NÓS é o time 0), a fase, o melhor de 1 ou de 3 e as partidas
// ganhas por cada trio. decidido: o confronto acabou e a sala não começa outra partida.
export interface CampeonatoDaSala {
  nome: string;
  slug: string;
  rodada: string; // "Semifinal", "Rodada 3"
  trios: [string, string];
  melhorDe: 1 | 3;
  vitorias: [number, number];
  decidido: boolean;
}

export interface PessoaNaPlateia { id: string; perfil: Perfil }

// ---- canal da plateia ----
// O que a plateia vê é montado campo a campo (services/mesa/src/plateia.ts), nunca copiando a visão de um lugar:
// cartas jogadas (a coberta sem a carta), vira, placar, pedidos de truco, resultado de rodada e de mão, rostos e
// apelidos de quem está sentado. Nunca: cartas da mão, carta coberta, sinais ou aviso de sinal, votos do trio e
// as cartas dos parceiros na mão de onze, mesmo na sala com Todos veem.
export interface JogadaNaPlateia { seat: Seat; card: Card | null; hidden: boolean }
export interface RodadaNaPlateia { leader: Seat; result: RoundResult | null; winnerSeat: Seat | null; plays: JogadaNaPlateia[] }
export interface MaoNaPlateia {
  vira: Card;
  manilha: Rank;
  value: HandValue;
  pending: Pending | null;
  turn: Seat;
  special: Special;
  onzeTeam: Team | null;
  ended: boolean;
  result: HandResult | null;
  rounds: RodadaNaPlateia[];
  cardCounts: number[];
}
export interface VisaoDaPlateia {
  phase: Phase;
  handNo: number;
  scores: [number, number];
  dealer: Seat;
  mao: Seat;
  winner: Team | null;
  hand: MaoNaPlateia | null;
}
export type EventoDaPlateia =
  | { type: 'hand_start'; handNo: number; dealer: Seat; vira: Card; manilha: Rank; special: Special; onzeTeam: Team | null }
  | { type: 'play'; seat: Seat; card: Card | null; hidden: boolean; round: number }
  | { type: 'round_end'; round: number; result: RoundResult; winnerSeat: Seat }
  | { type: 'truco'; seat: Seat; level: number }
  | { type: 'accept'; seat: Seat; level: number }
  | { type: 'run'; seat: Seat; level: number; points: number }
  | { type: 'raise'; seat: Seat; level: number; accepted: number }
  | { type: 'onze_play'; team: Team }
  | { type: 'onze_run'; team: Team }
  | { type: 'hand_end'; team: Team | null; points: number; reason: string; scores: number[] }
  | { type: 'game_over'; team: Team; scores: number[] };
export interface EventoDaPlateiaNumerado { seq: number; ev: EventoDaPlateia }

export interface LugarNaPlateia {
  tipo: 'pessoa' | 'bot';
  id?: string; // pessoa: para a foto do rosto
  apelido: string; // com bot no lugar de quem saiu ou caiu, o nome do bot
  avatar: string | null;
  conectado: boolean;
  bot?: true; // um bot joga no lugar de quem saiu ou caiu
  nivel?: number;
  moldura?: string;
  coroa?: true; // a coroa do Rei do Pote da semana passada
  vagaAberta?: true;
  entraNaMao?: true;
}

export interface SalaDaPlateia {
  codigo: string | null; // sala privada: sem o código, que dá acesso à sala
  nome: string;
  publica: boolean;
  fase: FaseSala;
  partida: number;
  donoApelido: string | null;
  lugares: (LugarNaPlateia | null)[];
  vagas: number; // lugares que aceitam gente agora: "Sentar na mesa"
  plateia: number; // quantos assistem
  atrasoMs: number; // campeonato: a plateia vê a mesa com atraso
  tempoPorJogadaMs: number;
  visual: { mesa: string; verso: string } | null;
}

// ---- painel admin: as salas ao vivo (GET /mesa/admin/salas e GET /mesa/admin/salas/CODIGO) ----
// Montado por Sala.naVisaoAdmin() a partir do que a plateia vê, mais os lugares com id, plano e conexão de cada pessoa.
// Nunca leva a mão de ninguém, carta coberta, sinal, aviso de sinal, voto do trio, confirmação de correr nem as cartas
// dos parceiros na mão de onze. Todo campo vem sempre (null quando não há), para o retrato das chaves ser um só.
// Tipo da sala: campeonato, liga, comunidade, valendo Tentos, aberta ou a privada de antes, nessa ordem de prioridade.
export type TipoDaSala = 'aberta' | 'tentos' | 'comunidade' | 'campeonato' | 'liga' | 'privada';
export type FaseNoAdmin = 'esperando' | 'jogando' | 'fim' | 'encerrada';
// conectado; caiu (lugar guardado durante a reconexão); ausente (lugar marcado de liga ou campeonato sem a pessoa);
// bot (o bot joga no lugar e a vaga não abre: valendo Tentos, campeonato ou fim de partida); vaga (vaga aberta, com o bot
// jogando); proxima_mao (quem sentou na vaga entra na próxima mão; até lá, o bot joga); livre (lugar vazio).
export type EstadoDoLugar = 'conectado' | 'caiu' | 'ausente' | 'bot' | 'vaga' | 'proxima_mao' | 'livre';

export interface PessoaNoAdmin { id: string; apelido: string; avatar: string | null }
export interface PessoaComPlano extends PessoaNoAdmin { plano: Plano | null } // null: o plano não veio do banco

export interface LugarNoAdmin {
  lugar: Seat;
  trio: Team;
  estado: EstadoDoLugar;
  // De quem é o lugar: quem joga, quem caiu, quem saiu e pode voltar à vaga, ou quem entra na próxima mão.
  pessoa: (PessoaComPlano & { conectado: boolean }) | null;
  bot: string | null; // Bot1, Bot2...: o bot que joga no lugar agora
  caiuEm: number | null; // estado caiu: quando a conexão caiu
  caiuHaS: number | null; // estado caiu: segundos desde a queda ("caiu há 20 s")
  entraNaMao: number | null; // estado proxima_mao: a mão em que a pessoa entra
}

export interface PlacarNoAdmin {
  trios: [string, string]; // "Trio de Bia" e "Trio de Zeca", como a plateia vê
  pontos: [number, number] | null; // jogando, o placar da partida; no fim, o placar final
  maoEmAndamento: { numero: number; valor: number; especial: 'onze' | 'ferro' | null } | null;
}

export interface SalaNoAdmin {
  id: string; // CODIGO-criadaEm: o mesmo prefixo das partidas na API (partidas_processadas.id)
  codigo: string;
  nome: string;
  tipo: TipoDaSala;
  publica: boolean;
  criadaEm: number;
  dono: { id: string; apelido: string | null; avatar: string | null } | null; // liga e campeonato não têm dono
  fase: FaseNoAdmin;
  faseDesde: number | null; // quando entrou na fase ("Jogando há 18 min"); null em sala guardada antes deste campo
  partida: number; // quantas partidas a sala já começou
  placar: PlacarNoAdmin;
  lugares: LugarNoAdmin[]; // sempre 6, na ordem dos lugares: o trio 0 nos pares, o trio 1 nos ímpares
  sentados: number; // pessoas sentadas (inclusive quem caiu e quem entra na próxima mão)
  bots: number; // lugares com bot jogando agora
  vagas: number; // lugares que aceitam gente agora (na espera, os livres; jogando, as vagas abertas)
  plateia: { total: number; aberta: boolean; atrasoMs: number };
  regras: HouseRules;
  foraDoPadrao: (keyof HouseRules)[]; // as regras da casa que fogem do padrão, para os chips do cartão
  tentos: { ingresso: number; pote: number; casa: number; premio: number } | null; // premio: por vencedor
  denuncias: { total: number; status: 'oculta' | 'removida' | null };
  encerraNoFim: boolean; // encerrada pelo painel (ou removida na revisão do nome) com a partida andando: fecha no fim dela
  comunidade: string | null; // id da comunidade da mesa
  campeonato: { nome: string; rodada: string; trios: [string, string]; melhorDe: 1 | 3; vitorias: [number, number]; decidido: boolean; transmissao: boolean } | null;
  liga: { nome: string } | null;
}

// O detalhe: o mesmo da lista, mais quem assiste e quem está de pé na sala de espera.
export interface SalaNoAdminDetalhe extends Omit<SalaNoAdmin, 'plateia'> {
  plateia: SalaNoAdmin['plateia'] & { pessoas: PessoaNoAdmin[] };
  emPe: PessoaComPlano[];
}

// O topo da tela Salas, sempre de todas as salas da mesa (antes dos filtros).
export interface TopoDasSalas {
  abertas: number; // salas que não encerraram
  jogando: number;
  esperando: number;
  sentadas: number; // pessoas sentadas
  bots: number; // bots jogando nas partidas em andamento
  vagas: number; // vagas abertas nas partidas em andamento
  assistindo: number;
}
export interface ListaDeSalasNoAdmin { agora: number; topo: TopoDasSalas; total: number; salas: SalaNoAdmin[] }
export interface DetalheDaSalaNoAdmin { agora: number; sala: SalaNoAdminDetalhe }
// POST /mesa/admin/salas/CODIGO/encerrar: jaEstava, a sala já estava marcada para encerrar (ou já encerrada) e nada foi gravado.
export interface EncerrarSalaNoAdmin { ok: true; codigo: string; fase: FaseNoAdmin; encerraNoFim: boolean; jaEstava: boolean }

// Trio fixo reunindo para a busca: quem do trio já pediu a busca.
export interface TrioNaBusca {
  id: string;
  nome: string;
  parceiros: { apelido: string; avatar: string | null }[];
}

export interface TrioReunindo {
  id: string;
  nome: string;
  membros: { usuario: string; apelido: string | null; pronto: boolean }[];
}

// A liga da mesa. Sem patrocinador, marca e prêmio são null e a tela de entrada não aparece.
export interface LigaDaSala {
  id: string;
  nome: string;
  marca: { nome: string; logo: string | null; cor: string | null } | null;
  premio: string | null; // prêmio do trio campeão da semana, dado pelo patrocinador
  inicio: number; // semana da liga: segunda 00h00 a domingo 23h59, em Brasília
  fim: number;
}

// Sala valendo Tentos: valores do pote calculados no servidor (calcularPote) e o resultado da última partida.
export interface TentosDaSala {
  ingresso: number;
  pote: number;
  casa: number;
  premio: number; // por vencedor
  reservados: string[]; // quem está com o ingresso reservado nesta sala
  jogadores: string[] | null; // os 6 que pagaram a partida em jogo ou a última
  saiu: string[]; // quem saiu da partida e perdeu a parte
  resultado: ResultadoTentos | null;
}

export interface ResultadoTentos {
  partida: number;
  vencedor: Team;
  premio: number;
  queima: number;
  recebem: string[];
  anulada: boolean;
}

// Uma sala pública na lista (GET /mesa/salas) ou o resumo de uma sala pelo código (GET /mesa/sala/CODIGO).
export interface SalaNaLista {
  id: string; // CODIGO-criadaEm: a denúncia vale para esta sala, mesmo que o código volte a ser usado
  codigo: string;
  nome: string;
  publica: boolean;
  fase: FaseSala;
  ingresso: number | null;
  premio: number | null;
  sentados: number;
  vagas: number; // lugares que aceitam gente agora: na espera, os livres; com a partida andando, as vagas abertas
  placar?: [number, number]; // partida em andamento: "Em andamento · 08 x 06"
  avatares: (string | null)[]; // de quem está sentado, para o cartão da lista
  regrasDaCasa: boolean;
  // As regras da sala, para os chips do cartão que mostram o que foge do padrão (salas de antes da lista não têm).
  regras?: RegrasNoCartao;
  // Só na lista de salas públicas: quem está sentado, para "Quem está na sala" (bot sem id, sem botão de amizade).
  jogadores?: JogadorNaLista[];
  // Só na lista, para quem pediu com a conta: os amigos dele sentados nesta sala (bot e quem está de pé não contam).
  amigosNaSala?: { id: string; apelido: string }[];
}
export interface RegrasNoCartao { sinais: SignalVisibility; zoeira: boolean; tempoPorJogadaMs: number; vagasComBot: boolean }
export type JogadorNaLista = { id: string; apelido: string; avatar: string | null } | { bot: true; apelido: string; vaga?: true };

// O trio que decide vê os votos de cada um e quem está na confirmação; o outro trio vê só que há votação.
// A confirmação ("Aceitar mesmo assim") só aparece no aparelho de quem votou aceitar contra a maioria.
export interface VotacaoVisivel {
  kind: VoteKind;
  team: Team;
  stage: 'vote' | 'confirm' | 'done';
  level: number;
  votes?: Record<number, VoteOption | null>;
  conf?: Record<number, ConfirmOption | null>;
  confirmer?: Seat | null;
  outcome?: { act: VoteAct; seat: Seat } | null;
  contam?: Seat[]; // quem decide: as pessoas do trio (ou os três, no trio só de bots)
  opinioes?: Record<number, VoteOption>; // opinião dos bots no trio com pessoa, fora da conta
}

export interface EventoNumerado {
  seq: number;
  ev: VisibleEvent;
}

export type CodigoErro =
  | 'protocolo' | 'nao_autenticado' | 'token_invalido' | 'conta_obrigatoria' | 'sala_inexistente' | 'sala_cheia'
  | 'fora_da_sala' | 'sem_permissao' | 'lugar_ocupado' | 'fora_de_hora' | 'acao_invalida' | 'outra_conexao'
  | 'muitas_mensagens' | 'sem_plano' | 'cedo_demais' | 'sala_encerrada'
  | 'sem_tentos' | 'tentos_bloqueados' | 'tentos_desligados' | 'nome_invalido'
  | 'sem_partidas' | 'limite_indisponivel' | 'liga_desligada' | 'trio_indisponivel'
  | 'plateia_cheia' | 'tirado_da_plateia' | 'so_membros' | 'zoeira_desligada'
  | 'falta_celular';

export type MensagemServidor =
  | { t: 'bemvindo'; usuario: string; anonimo: boolean; versao: number; agora: number }
  | { t: 'criada'; codigo: string }
  | {
    t: 'estado'; agora: number; sala: SalaPublica; eu: Seat | null; partida: number; seq: number;
    visao: PlayerView | null; votacao: VotacaoVisivel | null; prazos: Prazo[];
    zoeira?: boolean; // a sua chave "Zoeira na mesa", como a mesa leu (sem o campo: ligada)
  }
  | {
    t: 'atualizacao'; agora: number; partida: number; seq: number; eventos: EventoNumerado[];
    visao: PlayerView; votacao: VotacaoVisivel | null; prazos: Prazo[];
  }
  | { t: 'sala'; agora: number; sala: SalaPublica; prazos: Prazo[] }
  | { t: 'reacao'; lugar: Seat; emoji: string }
  | { t: 'opiniao'; lugar: Seat; emoji: string }
  | { t: 'frase'; lugar: Seat; frase: string; zoeira: boolean } // zoeira: da aba Zoeira (só chega a quem está com ela ligada)
  | { t: 'zoeira'; ligada: boolean } // a sua chave "Zoeira na mesa" mudou: vale da próxima mão em diante
  | { t: 'sentou'; lugar: Seat; apelido: string; no: string } // alguém sentou na vaga aberta: "Tati sentou no lugar do Bot1"
  | { t: 'erro'; codigo: CodigoErro; mensagem: string }
  | { t: 'pong'; agora: number }
  // Busca de mesa da liga: desde quando, de que divisão e a liga da semana. Pareado: a mesa de quem foi pareado.
  // Com trio fixo, os 2 parceiros vêm com apelido e rosto, para os lugares de NÓS.
  | { t: 'busca'; agora: number; desde: number; divisao: number; liga: LigaDaSala; trio: TrioNaBusca | null }
  // A fila em números: quantos procuram mesa (trio conta 3) e quantos, além de você, estão na sua faixa.
  // Nunca leva id, apelido, rosto ou divisão de outra pessoa.
  | { t: 'busca_fila'; total: number; naFaixa: number }
  // Mesa da liga em andamento com vaga aberta: o placar (o seu trio primeiro) e você entra na próxima mão.
  | { t: 'pareado'; codigo: string; andamento?: { nos: number; eles: number } }
  | { t: 'reunindo'; trio: TrioReunindo } // trio fixo na busca: esperando os 3 pedirem a busca
  | { t: 'ganhos'; partida: number; ganhos: GanhosDaPartida } // fim da partida: o que ela rendeu, só para você
  | { t: 'rodagem'; partida: number; lance: string; quanto: number; total: number } // um lance seu agora: a Rodagem sobe na hora
  | { t: 'busca_cancelada' }
  // Limite do Grátis: quantas partidas grátis sobram hoje, já descontada a que começou (ou a vaga em que sentou), e de
  // onde saiu a que começou. Só para quem está no Grátis.
  | { t: 'limite'; restantes: number; origem: 'diaria' | 'bonus' }
  // Canal da plateia: a mesa como a plateia vê. desde: o seq da mensagem anterior (os sinais não entram na conta).
  | { t: 'plateia_estado'; agora: number; sala: SalaDaPlateia; partida: number; seq: number; mesa: VisaoDaPlateia | null; prazos: Prazo[] }
  | { t: 'plateia_atualizacao'; agora: number; partida: number; desde: number; seq: number; eventos: EventoDaPlateiaNumerado[]; mesa: VisaoDaPlateia; prazos: Prazo[] }
  | { t: 'plateia_sala'; agora: number; sala: SalaDaPlateia }
  | { t: 'reacao_plateia'; emoji: string }; // a faixa Plateia, para quem joga e para quem assiste

// ---- leitura e validação do que chega do aparelho ----
export class ErroProtocolo extends Error {}

const isObj = (x: unknown): x is Record<string, unknown> => typeof x === 'object' && x !== null && !Array.isArray(x);
const isSeat = (x: unknown): x is Seat => Number.isInteger(x) && (x as number) >= 0 && (x as number) <= 5;
const isTexto = (x: unknown, max: number): x is string => typeof x === 'string' && x.length > 0 && x.length <= max;
const CARTA = /^[4567QJKA23][chsd]$/;
const CODIGO = /^[A-Z2-9]{6}$/;
const AVATAR = /^[a-z0-9-]{1,40}$/;
const VERSAO_GRITO = /^[a-z0-9]{1,12}$/;

function falha(msg: string): never { throw new ErroProtocolo(msg); }
const lerOrigem = (x: unknown): OrigemDaVaga => (typeof x === 'string' && (ORIGENS_DA_VAGA as readonly string[]).includes(x) ? x as OrigemDaVaga : falha('origem inválida'));

// Texto limpo: sem caracteres de controle, espaços colapsados, até `max` caracteres.
export function limparTexto(x: unknown, max: number): string {
  return typeof x === 'string' ? [...x.replace(/[\p{Cc}\p{Cf}]/gu, '').replace(/\s+/g, ' ').trim()].slice(0, max).join('').trim() : '';
}

// Apelido limpo, até 20 caracteres.
export function limparPerfil(p: unknown): Perfil {
  if (!isObj(p)) falha('perfil ausente');
  const limpo = limparTexto(p.apelido, 20);
  if (!limpo) falha('apelido vazio');
  // O banco só guarda apelido que passou pelo filtro. Se um aparelho alterado mandar outro, a mesa mostra "Jogador".
  const apelido = ofensivo(limpo) ? 'Jogador' : limpo;
  const avatar = p.avatar === null || p.avatar === undefined ? null : typeof p.avatar === 'string' && AVATAR.test(p.avatar) ? p.avatar : falha('avatar inválido');
  if (p.gritos === undefined || p.gritos === null) return { apelido, avatar };
  if (!isObj(p.gritos)) falha('gritos inválidos');
  const gritos: Partial<Record<Grito, string>> = {};
  for (const [k, v] of Object.entries(p.gritos)) {
    if (!(GRITOS as readonly string[]).includes(k) || typeof v !== 'string' || !VERSAO_GRITO.test(v)) falha('grito inválido');
    gritos[k as Grito] = v;
  }
  return Object.keys(gritos).length ? { apelido, avatar, gritos } : { apelido, avatar };
}

export function lerMensagem(texto: string): MensagemCliente {
  if (texto.length > TAMANHO_MAXIMO) falha('mensagem grande demais');
  let m: unknown;
  try { m = JSON.parse(texto); } catch { falha('JSON inválido'); }
  if (!isObj(m) || typeof m.t !== 'string') falha('mensagem sem tipo');
  switch (m.t) {
    case 'ola':
      if (!isTexto(m.token, 4000)) falha('token ausente');
      return { t: 'ola', token: m.token };
    case 'criar': {
      if (m.regras !== undefined && !isObj(m.regras)) falha('regras inválidas');
      const out: MensagemCliente & { t: 'criar' } = { t: 'criar' };
      if (m.regras !== undefined) out.regras = m.regras as Partial<HouseRules>;
      if (m.nome !== undefined) { if (typeof m.nome !== 'string' || m.nome.length > 100) falha('nome inválido'); const n = limparTexto(m.nome, 100); if (n) out.nome = n; } // o servidor valida tamanho e palavras
      if (m.publica !== undefined) { if (typeof m.publica !== 'boolean') falha('pública inválida'); out.publica = m.publica; }
      if (m.ingresso !== undefined && m.ingresso !== null) { if (!Number.isInteger(m.ingresso)) falha('ingresso inválido'); out.ingresso = m.ingresso as number; }
      if (m.plateia !== undefined) { if (typeof m.plateia !== 'boolean') falha('plateia inválida'); out.plateia = m.plateia; }
      if (m.comunidade !== undefined) { if (typeof m.comunidade !== 'string' || !/^[0-9a-f-]{36}$/.test(m.comunidade)) falha('comunidade inválida'); out.comunidade = m.comunidade; }
      return out;
    }
    case 'entrar': {
      if (typeof m.codigo !== 'string' || !CODIGO.test(m.codigo)) falha('código de sala inválido');
      const out: MensagemCliente & { t: 'entrar' } = { t: 'entrar', codigo: m.codigo, perfil: limparPerfil(m.perfil) };
      if (m.origem !== undefined) out.origem = lerOrigem(m.origem);
      return out;
    }
    case 'sentar': {
      if (!isSeat(m.lugar)) falha('lugar inválido');
      const out: MensagemCliente & { t: 'sentar' } = { t: 'sentar', lugar: m.lugar };
      if (m.origem !== undefined) out.origem = lerOrigem(m.origem);
      return out;
    }
    case 'jogar': {
      if (typeof m.carta !== 'string' || !CARTA.test(m.carta)) falha('carta inválida');
      const out: MensagemCliente & { t: 'jogar' } = { t: 'jogar', carta: m.carta as Card };
      if (m.coberta !== undefined) { if (typeof m.coberta !== 'boolean') falha('coberta inválida'); out.coberta = m.coberta; }
      return out;
    }
    case 'votar':
      if (m.opcao !== 'accept' && m.opcao !== 'run' && m.opcao !== 'raise') falha('voto inválido');
      return { t: 'votar', opcao: m.opcao };
    case 'confirmar':
      if (m.opcao !== 'insist' && m.opcao !== 'run') falha('confirmação inválida');
      return { t: 'confirmar', opcao: m.opcao };
    case 'sinal':
      // O sinal vai sempre para os dois parceiros: o `para` de um aparelho com a versão antiga é ignorado.
      if (typeof m.gesto !== 'string' || !(GESTURES as readonly string[]).includes(m.gesto)) falha('gesto inválido');
      return { t: 'sinal', gesto: m.gesto as Gesture };
    case 'reacao':
      if (typeof m.emoji !== 'string' || !(REACOES as readonly string[]).includes(m.emoji)) falha('reação inválida');
      return { t: 'reacao', emoji: m.emoji };
    case 'opiniao':
      if (typeof m.emoji !== 'string' || !(OPINIOES as readonly string[]).includes(m.emoji)) falha('opinião inválida');
      return { t: 'opiniao', emoji: m.emoji };
    case 'frase':
      // Só o texto exato de uma das duas listas: nada de espaço a mais nem de maiúsculas trocadas.
      if (typeof m.frase !== 'string' || !((FRASES as readonly string[]).includes(m.frase) || ehZoeira(m.frase))) falha('frase inválida');
      return { t: 'frase', frase: m.frase };
    case 'assistir': {
      if (m.codigo !== undefined) {
        if (typeof m.codigo !== 'string' || !CODIGO.test(m.codigo)) falha('código de sala inválido');
        return { t: 'assistir', codigo: m.codigo, perfil: limparPerfil(m.perfil) };
      }
      if (typeof m.amigo !== 'string' || !/^[A-Za-z0-9_-]{1,64}$/.test(m.amigo)) falha('sala ou amigo ausente');
      return { t: 'assistir', amigo: m.amigo, perfil: limparPerfil(m.perfil) };
    }
    case 'reacao_plateia':
      if (typeof m.emoji !== 'string' || !(REACOES as readonly string[]).includes(m.emoji)) falha('reação inválida');
      return { t: 'reacao_plateia', emoji: m.emoji };
    case 'tirar_da_plateia':
      if (typeof m.usuario !== 'string' || !/^[A-Za-z0-9_-]{1,64}$/.test(m.usuario)) falha('usuário inválido');
      return { t: 'tirar_da_plateia', usuario: m.usuario };
    case 'revanche':
      if (typeof m.sim !== 'boolean') falha('resposta da revanche inválida');
      return { t: 'revanche', sim: m.sim };
    case 'bots':
      if (typeof m.sim !== 'boolean') falha('resposta inválida');
      return { t: 'bots', sim: m.sim };
    case 'campeonato':
      if (typeof m.jogo !== 'string' || !/^[0-9a-f-]{36}$/.test(m.jogo)) falha('jogo inválido');
      return { t: 'campeonato', jogo: m.jogo };
    case 'buscar': {
      if (m.trio !== undefined && (typeof m.trio !== 'string' || !/^[0-9a-f-]{1,40}$/.test(m.trio))) throw new ErroProtocolo('trio inválido');
      return m.trio === undefined ? { t: 'buscar', perfil: limparPerfil(m.perfil) } : { t: 'buscar', perfil: limparPerfil(m.perfil), trio: m.trio };
    }
    case 'levantar': case 'iniciar': case 'sair': case 'truco': case 'reservar': case 'sincronizar': case 'ping': case 'cancelar_busca':
      return { t: m.t };
    case 'recebi':
      if (!Number.isInteger(m.seq) || (m.seq as number) < 0) return falha('seq inválido');
      return { t: 'recebi', seq: m.seq as number };
    default:
      return falha('tipo desconhecido: ' + String(m.t).slice(0, 20));
  }
}
